/**
 * The player stats router works for the organization management for the app.
 * The player stats router use the root player stats as the module scope.
 * It use the tbs.view.Scene as the plugin to improve the development
 * base on the vue framework. It treats the following view as the scene
 * instance.
 *
 * The TBS Scene plugin introduces the properties, mediator, convertor, 
 * translator properties for the built-in meta object.
 *
 * The properties property stands for the context object with the scene view.
 * It contains the title, route, router, bridge* and so on which is used into
 * the mediator.
 *
 * The mediator property stands for the business controller with the scene
 * view. We treat the view as the special component to maintain the whole
 * component-based development pattern. Therefore, it separate the business
 * logic from view to mediator.
 *
 * The convertor property stands for the data convertor between view object
 * and entity object. We treat the data via requesting the service API as the 
 * entity object which is the persistence data and also treat the data via 
 * rendering the UI component as the view object.
 *
 * The translator property stands for the dictionary feature and also acts as the
 * simple version for the i18n module.
 */
const PlayerStatsRouter = {
    path: '/player-stats',
    name: 'PlayerStats',
    component: () => import('@/tbs/core/PlayerStatsScene'),
    children: [
        {
            path: 'rankings',
            name: 'PlayerStatsListRanking',
            meta: {
                properties: {
                    title: '球员统计排行榜',
                },
                mediator: () => import('@/tbs/controller/RankingMediator'),
                convertor: () => import('@/tbs/model/vo/Ranking'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/RankingList'),
        },
        {
            path: 'rankings/skaters',
            name: 'PlayerStatsListSkaterRanking',
            meta: {
                properties: {
                    title: '队员统计排行榜'
                },
                mediator: () => import('@/tbs/controller/SkaterRankingMediator'),
                convertor: () => import('@/tbs/model/vo/SkaterRanking'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/SkaterRankingList'),
        },
        {
            path: 'rankings/goalies',
            name: 'PlayerStatsListGoalieRanking',
            meta: {
                properties: {
                    title: '守门员统计排行榜'
                },
                mediator: () => import('@/tbs/controller/GoalieRankingMediator'),
                convertor: () => import('@/tbs/model/vo/GoalieRanking'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/GoalieRankingList'),
        },
        {
            path: 'search',
            name: 'PlayerStatsListSearch',
            meta: {
                properties: {
                    title: '球员搜索结果 - keyword',
                    query: 'keyword'
                },
                mediator: () => import('@/tbs/controller/SearchMediator'),
                convertor: () => import('@/tbs/model/vo/Search'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/SearchList'),
        },
        {
            path: 'player/:id/facts',
            name: 'PlayerStatsDetailPlayerFact',
            meta: {
                properties: {
                    title: '查看基本信息 - name',
                    query: 'name'
                },
                mediator: () => import('@/tbs/controller/PlayerFactMediator'),
                convertor: () => import('@/tbs/model/vo/PlayerFact'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/PlayerDetailFact'),
        },
        {
            path: 'player/:id/stats',
            name: 'PlayerStatsDetailPlayerStat',
            meta: {
                properties: {
                    title: '查看技术统计 - name',
                    query: 'name'
                },
                mediator: () => import('@/tbs/controller/PlayerStatMediator'),
                convertor: () => import('@/tbs/model/vo/PlayerStat'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/PlayerDetailStat'),
        },
        {
            path: 'player/:id/games',
            name: 'PlayerStatsDetailPlayerGame',
            meta: {
                properties: {
                    title: '查看球员比赛 - name',
                    query: 'name'
                },
                mediator: () => import('@/tbs/controller/PlayerGameMediator'),
                convertor: () => import('@/tbs/model/vo/PlayerGame'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/PlayerDetailGame'),
        },
        {
            path: 'team/:id/rosters',
            name: 'PlayerStatsDetailTeamRoster',
            meta: {
                properties: {
                    title: '查看球队球员 - name',
                    query: 'name'
                },
                mediator: () => import('@/tbs/controller/TeamRosterMediator'),
                convertor: () => import('@/tbs/model/vo/TeamRoster'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/TeamDetailRoster'),
        },
        {
            path: 'team/:id/stats',
            name: 'PlayerStatsDetailTeamStat',
            meta: {
                properties: {
                    title: '查看球队统计 - name',
                    query: 'name'
                },
                mediator: () => import('@/tbs/controller/TeamStatMediator'),
                convertor: () => import('@/tbs/model/vo/TeamStat'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/TeamDetailStat'),
        },
        {
            path: 'team/:id/games',
            name: 'PlayerStatsDetailTeamGame',
            meta: {
                properties: {
                    title: '查看球队比赛 - name',
                    query: 'name'
                },
                mediator: () => import('@/tbs/controller/TeamGameMediator'),
                convertor: () => import('@/tbs/model/vo/TeamGame'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/TeamDetailGame'),
        },
        {
            path: 'team/:id/rankings',
            name: 'PlayerStatsDetailTeamRanking',
            meta: {
                properties: {
                    title: '查看球队排行 - name',
                    query: 'name'
                },
                mediator: () => import('@/tbs/controller/TeamRankingMediator'),
                convertor: () => import('@/tbs/model/vo/TeamRanking'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/TeamDetailRanking'),
        },
        {
            path: 'league/:id/stats',
            name: 'PlayerStatsDetailLeagueStat',
            meta: {
                properties: {
                    title: '查看联盟统计 - name',
                    query: 'name'
                },
                mediator: () => import('@/tbs/controller/LeagueStatMediator'),
                convertor: () => import('@/tbs/model/vo/LeagueStat'),
                translator:() => import('@/tbs/model/to/Stats')
            },
            component: () => import('@/views/player-stats/LeagueDetailStat'),
        },
    ],
    hidden: true,
}

export default PlayerStatsRouter